<template>
  <div class="container">
    <h1>Services</h1>
    <b-row>
      <b-col>
        <b-form class="d-flex mb-2" inline @submit.prevent>
          <b-form-select
            id="services__perPage"
            class="mb-0"
            v-model="perPage"
            :options="perPageOptions"
            aria-label="Per page"
            aria-controls="services-table"
          ></b-form-select>
          <b-form-input
            id="services__searchQuery"
            class="ml-sm-2 col-md-2 mb-0"
            v-model="searchQuery"
            placeholder="Search..."
            aria-label="Search"
            aria-controls="services-table"
          ></b-form-input>
          <router-link
            v-if="services.length"
            class="ml-auto btn btn-primary"
            :to="{ name: 'services/add' }"
          >
            Add Service
          </router-link>
        </b-form>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-table
          id="services__table"
          :items="services"
          :fields="fields"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :filter="searchQuery"
          :per-page="perPage"
          :current-page="currentPage"
          :busy="loading"
          :show-empty="!loading && !services.length"
          stacked="md"
          striped
          @filtered="
            filteredItems => (searchItemsLength = filteredItems.length)
          "
        >
          <template v-slot:cell(name)="data">
            <router-link
              tag="a"
              :to="{ name: 'services/edit', params: { id: data.item._id } }"
              >{{ data.item.name }}</router-link
            >
          </template>
          <template v-slot:cell(actions)="data">
            <b-button
              size="sm"
              variant="link"
              class="m-0 p-0 text-muted"
              @click.prevent="confirmDeleteService(data.item)"
            >
              <i class="fa fa-trash"></i>
            </b-button>
          </template>
          <template v-slot:table-busy>
            <div class="text-center text-primary my-2">
              <b-spinner class="align-middle"></b-spinner>
            </div>
          </template>
          <template v-slot:empty>
            <div v-if="error" class="text-center text-danger my-2">
              <p>Something went wrong, please try again later.</p>
            </div>
            <div v-else class="text-center text-primary my-2">
              <p>You don't have any Services yet.</p>
              <router-link
                :to="{ name: 'services/add' }"
                class="btn btn-primary"
              >
                Add a Service
              </router-link>
            </div>
          </template>
        </b-table>
        <b-pagination
          id="services__pagination"
          v-model="currentPage"
          :total-rows="services.length"
          :per-page="perPage"
          align="right"
          aria-controls="services-table"
          first-number
          last-number
        ></b-pagination>
      </b-col>
    </b-row>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';
import { timestampToDateAndTime } from '@/time';

export default {
  name: 'AdminServices',
  data() {
    return {
      loading: true,
      error: null,
      fields: [
        {
          key: 'name',
          label: 'Name',
          sortable: true
        },
        {
          key: 'baseDuration',
          label: 'Duration (mins)',
          sortable: true
        },
        {
          key: 'enabled',
          label: 'Currently Available',
          sortable: true,
          formatter: value => (value ? 'Yes' : 'No')
        },
        {
          key: 'hidden',
          label: 'Hidden from Customers',
          sortable: true,
          formatter: value => (value ? 'Yes' : 'No')
        },
        {
          key: 'timestamp',
          label: 'Last Modified',
          sortable: true,
          formatter: timestampToDateAndTime
        },
        {
          key: 'actions',
          label: '',
          sortable: false
        }
      ],
      searchQuery: null,
      searchItemsLength: 0,
      sortBy: 'name',
      sortDesc: false,
      perPage: 5,
      currentPage: 1,
      perPageOptions: [5, 10, 25, 50]
    };
  },
  methods: {
    ...mapActions({
      fetchServices: 'services/fetchServices',
      deleteService: 'services/deleteService'
    }),
    async confirmDeleteService(service) {
      let result = await this.$swal({
        type: 'warning',
        title: 'Are you sure?',
        text: `The Service "${service.name}" will be permanently deleted.`,
        showCancelButton: true
      });

      if (!result.value) return;

      try {
        await this.deleteService(service);
        this.$notify({
          type: 'success',
          title: 'Success',
          text: 'Service deleted.'
        });
      } catch (e) {
        this.$notify({
          type: 'error',
          title: 'Error',
          text: e.message
        });
      }
    }
  },
  computed: mapGetters({
    services: 'services/servicesList'
  }),
  async created() {
    try {
      await this.fetchServices();
    } catch (e) {
      this.error = e.message;
    } finally {
      this.loading = false;
    }
  }
};
</script>
